import { useCallback, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  Button,
  TextField,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import accountService from '../services/account.service';
import userService from '../services/user.service';
import { loading } from '../reducers/general.reducer';

export default function Page() {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [accounts, setAccounts] = useState([]);
  const [data, setData] = useState({
    id: '',
    name: '',
    role: '',
    account: '',
    active: false,
  });

  const fetchData = useCallback(async () => {
    dispatch(loading(true));
    const resAccounts = await accountService.getAll();
    setAccounts(resAccounts);
    const resUser = await userService.get(id);
    setData(resUser);
    dispatch(loading(false));
  }, [dispatch, id]);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [fetchData, id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: data.id,
      name: data.name,
      role: data.role,
      account: data.account,
      submit: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().max(255).required('O NOME é obrigatório'),
      role: Yup.string().max(255).required('O ROLE é obrigatório'),
      account: Yup.string().max(255).required('O CONTA é obrigatório'),
    }),
    onSubmit: async (values, helpers) => {
      console.log(values);
      try {
        dispatch(loading(true));
        await userService.edit(id, {
          id,
          name: values.name,
          role: values.role,
          account: values.account,
          active: data.active,
        });
        dispatch(loading(false));
        navigate(-1);
      } catch (error) {
        let message = 'Ops! Algo de errado aconteceu';
        if (error.response && error.response.data && error.response.data.message[0]) {
          message = error.response.data.message.join('\r\n');
        }
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: message });
        helpers.setSubmitting(false);
      }
    },
  });

  return (
    <>
      <Helmet>
        <title> Editar Usuário | BMENU </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" gutterBottom>
            USUÁRIOS
          </Typography>
        </Stack>
        <Breadcrumbs aria-label="breadcrumb" mb={5}>
          <Link underline="hover" color="inherit" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
            Dashboard
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/users')}
            style={{ cursor: 'pointer' }}
          >
            Usuários
          </Link>
          <Typography color="text.primary">Editar</Typography>
        </Breadcrumbs>
        <Card>
          <CardContent>
            <Box>
              <form noValidate onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      error={!!(formik.touched.id && formik.errors.id)}
                      fullWidth
                      helperText={formik.touched.id && formik.errors.id}
                      label="ID"
                      name="id"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.id}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      error={!!(formik.touched.name && formik.errors.name)}
                      fullWidth
                      helperText={formik.touched.name && formik.errors.name}
                      label="NOME"
                      name="name"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.name}
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth error={!!(formik.touched.role && formik.errors.role)}>
                      <InputLabel id="space-label">ROLE</InputLabel>
                      <Select
                        fullWidth
                        labelId="space-label"
                        label="ROLE"
                        name="role"
                        onBlur={formik.handleBlur}
                        onChange={(ev) => {
                          formik.setFieldValue('role', ev.target.value);
                          formik.setFieldValue('account', ev.target.value === 'admin' ? 'admin' : '');
                        }}
                        value={formik.values.role}
                      >
                        <MenuItem value="">
                          <em>Selecione</em>
                        </MenuItem>
                        <MenuItem value="admin">
                          <b>ADMIN</b>
                        </MenuItem>
                        <MenuItem value="user">User</MenuItem>
                      </Select>
                      <FormHelperText>{formik.touched.role && formik.errors.role}</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth error={!!(formik.touched.account && formik.errors.account)}>
                      <InputLabel id="space-label">CONTA</InputLabel>
                      <Select
                        fullWidth
                        labelId="space-label"
                        label="CONTA"
                        name="account"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.account}
                        disabled={formik.values.role === 'admin'}
                      >
                        <MenuItem value="">
                          <em>Selecione</em>
                        </MenuItem>
                        {formik.values.role === 'admin' && (
                          <MenuItem value="admin">
                            <b>ADMIN</b>
                          </MenuItem>
                        )}
                        {accounts.map((account, i) => (
                          <MenuItem key={`account-${i}`} value={account.id}>
                            {account.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{formik.touched.account && formik.errors.account}</FormHelperText>
                    </FormControl>
                  </Grid>
                  {formik.errors.submit && (
                    <Grid item xs={12} textAlign={'center'}>
                      <Typography color="error" variant="body2" style={{ whiteSpace: 'pre-line' }}>
                        {formik.errors.submit}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} textAlign={'right'}>
                    <Switch
                      checked={data && data.active}
                      color="success"
                      onChange={() => {
                        setData({ ...data, active: !data.active });
                      }}
                      name="active"
                    />
                    <Button variant="contained" type="submit">
                      EDITAR
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </CardContent>
          <Divider />
        </Card>
        {/* <Stack direction="row" alignItems="center" justifyContent="center" mt={5}>
          <Button variant="text" size="small" onClick={() => navigate(-1)}>
            VOLTAR
          </Button>
        </Stack> */}
      </Container>
    </>
  );
}
